import type { MatrixSnapshot } from "@/services/api/agentic-v2/types";
import {
  ANNUAL_REVIEW_MISSION_STEPS,
  type AnnualReviewMissionStepKey,
  type MissionStepOwnerRole,
} from "./annualReviewMission";
import type { HomeTask } from "./types";
import type { MatrixCell } from "./workloadMatrix";

export type MissionMatrixStep = {
  key: string;
  stageKey: string;
  label: string;
  fullLabel: string;
  ownerRole: MissionStepOwnerRole;
};

const MATRIX_ACRONYMS: Record<string, string> = {
  ff: "FF",
  ss: "SS",
  loas: "LOAs",
  loa: "LOA",
  tob: "ToB",
  ps: "PS",
  as: "AS",
  crm: "CRM",
  dg: "DG",
};

/** Major mission stage labels — mirrors API STAGE_MATRIX_LABELS. */
const STAGE_MATRIX_LABELS: Record<string, string> = {
  intro_and_ff: "Intro & fact find",
  ss_prep: "Starter session",
  loas: "LOAs",
  data_gathering: "Data gathering",
  narrative: "Planning & narrative",
  advice_session: "Advice session",
  intake: "Meeting",
  transcription: "Transcription",
  review: "Review",
  extraction: "Extractions",
  handover: "Handover",
  draft: "Letter",
  crm_push: "CRM",
};

/** Human label for a task_graph key — preserves FF, SS, LOAs, etc. */
export function formatMatrixNodeLabel(key: string): string {
  return key
    .split("_")
    .map((word) => MATRIX_ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Map task_graph node keys to mission stage rows (when matrix_mode is stage). */
const NODE_TO_STAGE: Record<string, string> = {
  check_ff: "intro_and_ff",
  monitor_ff: "intro_and_ff",
  complete_intro_outcome: "intro_and_ff",
  book_starter_session: "ss_prep",
  generate_ss: "ss_prep",
  prepare_for_ss: "ss_prep",
  complete_ss_outcome: "ss_prep",
  chase_tob_signed: "loas",
  generate_loas: "loas",
  send_loas_to_client: "loas",
  send_loas_to_providers: "loas",
  rerun_loas: "loas",
  data_gathering: "data_gathering",
  book_planning_session: "data_gathering",
  complete_ps_outcome: "data_gathering",
  draft_narrative: "narrative",
  push_narrative_to_plannr: "narrative",
  book_advice_session: "advice_session",
  draft_suitability_letter: "advice_session",
  generate_advice_pack: "advice_session",
  complete_as_outcome: "advice_session",
};

function matrixStepLabel(key: string, apiLabel: string): string {
  return STAGE_MATRIX_LABELS[key] ?? apiLabel ?? formatMatrixNodeLabel(key);
}

const LEGACY_CATEGORY_TO_STEP: Partial<
  Record<HomeTask["category"], AnnualReviewMissionStepKey>
> = {
  letter: "draft_short_letter",
  crm_updates: "propose_crm_updates",
  handover_notes: "draft_handover_notes",
  narrative_review: "review_extractions",
};

/** Resolve which matrix row a task belongs to, matching snapshot step keys. */
export function resolveMatrixRowKey(
  task: HomeTask,
  missionSteps?: MissionMatrixStep[],
): string | undefined {
  const raw =
    task.matrixRowKey ??
    task.missionStepKey ??
    LEGACY_CATEGORY_TO_STEP[task.category];
  if (!raw) return undefined;

  if (!missionSteps?.length) return raw;

  const stepKeys = new Set(missionSteps.map((step) => step.key));
  if (stepKeys.has(raw)) return raw;

  const staged = NODE_TO_STAGE[raw];
  if (staged) {
    if (stepKeys.has(staged)) return staged;
    const stageRow = missionSteps.find((step) => step.stageKey === staged);
    if (stageRow) return stageRow.key;
  }

  return undefined;
}

function parseOwnerRole(value: string | null | undefined): MissionStepOwnerRole {
  if (
    value === "adviser" ||
    value === "paraplanner" ||
    value === "administrator" ||
    value === "agent"
  ) {
    return value;
  }
  return "paraplanner";
}

/** Mission matrix rows from the surface snapshot, with Saltus annual-review fallback. */
export function missionStepsFromSnapshot(
  matrix: MatrixSnapshot | null | undefined,
): MissionMatrixStep[] {
  if (matrix?.steps?.length) {
    return matrix.steps.map((step) => {
      const label = matrixStepLabel(step.key, step.label);
      return {
        key: step.key,
        stageKey: step.stage_key ?? "other",
        label,
        fullLabel: label,
        ownerRole: parseOwnerRole(step.owner_role),
      };
    });
  }
  return [...ANNUAL_REVIEW_MISSION_STEPS];
}

export function emptyWorkloadMatrixFromSteps(steps: MissionMatrixStep[]): Record<string, MatrixCell> {
  return Object.fromEntries(
    steps.map((step) => [step.key, { done: 0, due: 0, chasing: 0, stale: 0 }]),
  );
}

export function missionStepByKey(
  steps: MissionMatrixStep[],
  key: string,
): MissionMatrixStep | undefined {
  const fromSteps = steps.find((step) => step.key === key);
  if (fromSteps) return fromSteps;
  return {
    key,
    stageKey: "other",
    label: formatMatrixNodeLabel(key),
    fullLabel: formatMatrixNodeLabel(key),
    ownerRole: "paraplanner",
  };
}
